import { Search as SearchIcon, X } from 'lucide-react';
import { cn } from '@rocco/utils/cn';

/**
 * The two pill heights the item tabs use: `sm` is the 42px control row the
 * desktop bars share with Sort and Display, `lg` the 52px row mobile sets
 * beside the Refine circle.
 */
type SearchPillSize = 'sm' | 'lg';

interface ProfessionalTabSearchPillProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    size?: SearchPillSize;
    /** `dark` inverts the pill for a dark tab. */
    theme?: 'light' | 'dark';
    /** Width and placement — the pill itself only owns its height and ink. */
    className?: string;
}

/**
 * The search pill every professional item tab carries.
 *
 * One definition because three tabs previously hand-copied it and two of the
 * copies lost the trailing button: Videos and Articles left the reader
 * selecting the text and deleting it while Products, Projects, Network and
 * Downloads cleared in one press.
 *
 * The trailing circle is a clear affordance, not a submit — these tabs filter
 * as you type — so it shows a magnifier until there is something to clear.
 */
export const ProfessionalTabSearchPill = ({
    value,
    onChange,
    placeholder = 'Search',
    size = 'sm',
    theme = 'light',
    className,
}: ProfessionalTabSearchPillProps) => {
    const isDark = theme === 'dark';
    const isLarge = size === 'lg';

    return (
        <div
            className={cn(
                'flex items-center justify-between rounded-full',
                isLarge
                    ? 'py-[0.5625rem] pr-[0.5625rem] pl-5'
                    : 'py-[0.3125rem] pr-[0.3125rem] pl-[1.25rem]',
                isDark
                    ? isLarge
                        ? 'bg-[#323232]'
                        : 'bg-white/[0.08]'
                    : 'bg-white',
                className,
            )}
        >
            <div className="flex min-w-0 flex-1 items-center gap-[0.6875rem]">
                <SearchIcon
                    className={cn(
                        'shrink-0',
                        isLarge ? 'size-[1.29rem]' : 'size-[1.125rem]',
                        isDark
                            ? isLarge
                                ? 'text-white/70'
                                : 'text-white/40'
                            : 'text-[#1f1f21]/40',
                    )}
                />
                <input
                    type="search"
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={placeholder}
                    className={cn(
                        'w-full min-w-0 border-0 bg-transparent leading-none outline-none [&::-webkit-search-cancel-button]:appearance-none',
                        // Literal 16px on the tall pill: anything under makes
                        // iOS Safari zoom the page on focus.
                        isLarge
                            ? 'font-sans text-[16px]'
                            : 'text-[1rem] tracking-[-0.025rem]',
                        isDark
                            ? isLarge
                                ? 'text-white placeholder:text-white/50'
                                : 'text-white placeholder:text-white/40'
                            : isLarge
                              ? 'text-[#1f1f21] placeholder:text-[#767676]'
                              : 'text-[#1f1f21] placeholder:text-[#1f1f21]/40',
                    )}
                />
            </div>
            <button
                type="button"
                aria-label={value ? 'Clear search' : 'Search'}
                onClick={() => value && onChange('')}
                className={cn(
                    'flex shrink-0 items-center justify-center rounded-full transition-colors',
                    isLarge ? 'size-[2.5rem]' : 'size-8',
                    isDark
                        ? 'bg-white/[0.14] text-white hover:bg-white/20'
                        : 'bg-[#181818] text-white hover:bg-[#2d2d2d]',
                )}
            >
                {value ? (
                    <X className="size-[1.05rem]" />
                ) : (
                    <SearchIcon className="size-[1.05rem]" />
                )}
            </button>
        </div>
    );
};
